import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import GroupsIcon from "@mui/icons-material/Groups";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Paper, Stack, Typography, } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useDeleteDivision, useRetrieveDivisionsBySeasonId, } from "../api/DivisionApiProvider";
import { useRetrieveLeagueTeams } from "../api/LeagueApiProvider";
import { useAssignTeamsToDivisions } from "../api/SeasonApiProvider";
import { EmptyState } from "../common/EmptyState";
import { Loading } from "../nav/Loading";
import { TeamAvatar } from "../teams/TeamAvatar";
import { DeleteDialog } from "../utils/DeleteDialog";
export const DivisionManagement = ({ season, canModify, onDeleteSuccess, onDeleteError, onHasChangesChange, onSave, showActions = true, }) => {
    const navigate = useNavigate();
    const { data: divisions, isLoading: divisionsLoading, error: divisionsError, } = useRetrieveDivisionsBySeasonId({
        seasonId: season.id,
    });
    const { data: leagueTeams, isLoading: teamsLoading, error: teamsError, } = useRetrieveLeagueTeams({
        leagueId: season.league,
        enabled: canModify,
    });
    // Local state to track division assignments before saving
    const [localDivisionAssignments, setLocalDivisionAssignments] = React.useState({});
    const [hasChanges, setHasChanges] = React.useState(false);
    const [isInitialized, setIsInitialized] = React.useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
    const [divisionToDelete, setDivisionToDelete] = React.useState(null);
    const [confirmDialog, setConfirmDialog] = React.useState({ open: false, title: "", message: "", onConfirm: () => { } });
    // Initialize local assignments when divisions first load (only once)
    React.useEffect(() => {
        if (divisions && divisions.length > 0 && !isInitialized) {
            const initialAssignments = {};
            divisions.forEach((division) => {
                initialAssignments[division.id] = division.teams || [];
            });
            setLocalDivisionAssignments(initialAssignments);
            setIsInitialized(true);
        }
    }, [divisions, isInitialized]);
    // Notify parent when hasChanges changes
    React.useEffect(() => {
        onHasChangesChange?.(hasChanges);
    }, [hasChanges, onHasChangesChange]);
    const assignTeamsMutation = useAssignTeamsToDivisions({
        seasonId: season.id,
        onSuccess: () => {
            onDeleteSuccess("Teams assigned successfully");
            setHasChanges(false);
            onSave?.();
        },
        onError: () => {
            onDeleteError("Failed to assign teams");
        },
    });
    const deleteDivisionMutation = useDeleteDivision({
        onSuccess: () => onDeleteSuccess("Division deleted successfully"),
        onError: () => {
            onDeleteError("Failed to delete division");
        },
    });
    const handleDeleteDivision = (divisionId) => {
        setDivisionToDelete(divisionId);
        setShowDeleteDialog(true);
    };
    const handleConfirmDelete = () => {
        if (divisionToDelete) {
            const id = divisionToDelete;
            deleteDivisionMutation.mutate({ id, seasonId: season.id }, {
                // Only drop the division from local state once the server confirms the
                // delete. Doing it optimistically left a still-existing division out
                // of the save payload on failure, which the backend rejects (it
                // requires the complete division set).
                onSuccess: () => {
                    setLocalDivisionAssignments((prev) => {
                        const updated = { ...prev };
                        delete updated[id];
                        return updated;
                    });
                },
            });
        }
        setShowDeleteDialog(false);
        setDivisionToDelete(null);
    };
    const showConfirmDialog = (title, message, onConfirm) => {
        setConfirmDialog({ open: true, title, message, onConfirm });
    };
    const handleConfirmClose = (confirmed) => {
        if (confirmed) {
            confirmDialog.onConfirm();
        }
        setConfirmDialog({
            open: false,
            title: "",
            message: "",
            onConfirm: () => { },
        });
    };
    // Navigate away, guarding unsaved assignment changes.
    const navigateWithGuard = (url) => {
        if (hasChanges) {
            showConfirmDialog("Unsaved Changes", "You have unsaved changes. Are you sure you want to navigate away? Your changes will be lost.", () => navigate(url));
            return;
        }
        navigate(url);
    };
    const handleEditDivision = (divisionId) => {
        navigateWithGuard(`/leagues/${season.league}/seasons/${season.id}/divisions/${divisionId}/edit`);
    };
    const handleAddDivision = () => {
        navigateWithGuard(`/leagues/${season.league}/seasons/${season.id}/divisions/create`);
    };
    // Which division (if any) a team is currently assigned to in local state.
    const getTeamDivisionId = (teamId) => {
        for (const [divId, teams] of Object.entries(localDivisionAssignments)) {
            if (teams.some((t) => t.id === teamId)) {
                return parseInt(divId);
            }
        }
        return null;
    };
    // Move a team to a division (or unassign when divisionId is null). A team can
    // only be in one division, so it's removed from all others first.
    const handleAssignTeam = (team, divisionId) => {
        if (!isInitialized || !team.id) {
            return;
        }
        setLocalDivisionAssignments((prev) => {
            const updated = { ...prev };
            Object.keys(updated).forEach((divId) => {
                const numericDivId = parseInt(divId);
                if (updated[numericDivId]) {
                    updated[numericDivId] = updated[numericDivId].filter((t) => t.id !== team.id);
                }
            });
            if (divisionId !== null) {
                updated[divisionId] = [...(updated[divisionId] || []), team];
            }
            return updated;
        });
        setHasChanges(true);
    };
    const handleSaveChanges = () => {
        const payload = Object.entries(localDivisionAssignments).map(([divisionId, teams]) => ({
            id: parseInt(divisionId),
            teamIds: teams.map((team) => team.id),
        }));
        assignTeamsMutation.mutate({ divisions: payload });
    };
    const handleResetChanges = React.useCallback(() => {
        if (divisions) {
            const initialAssignments = {};
            divisions.forEach((division) => {
                initialAssignments[division.id] = division.teams || [];
            });
            setLocalDivisionAssignments(initialAssignments);
            setHasChanges(false);
        }
    }, [divisions]);
    // Active teams in the league, alphabetized.
    const activeTeams = (leagueTeams || [])
        .filter((membership) => membership.isActive && membership.team?.id)
        .map((membership) => membership.team)
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    // The "bench": active teams not yet assigned to any division.
    const unassignedTeams = activeTeams.filter((team) => getTeamDivisionId(team.id) === null);
    const addTeamsUrl = `/leagues/${season.league}/teams/add/?returnTo=${encodeURIComponent(`/leagues/${season.league}/seasons/${season.id}/?step=0`)}`;
    return (_jsxs(Box, { children: [(divisionsLoading || teamsLoading) && _jsx(Loading, {}), divisions && divisions.length === 0 && (_jsx(EmptyState, { icon: _jsx(AccountTreeIcon, {}), heading: "No divisions yet", description: "Create your first division to start organizing teams.", actionLabel: canModify ? "Create First Division" : undefined, actionTo: canModify
                    ? `/leagues/${season.league}/seasons/${season.id}/divisions/create`
                    : undefined })), divisions && divisions.length > 0 && (_jsxs(Stack, { spacing: 3, children: [_jsxs(Stack, { direction: "row", justifyContent: "space-between", alignItems: "center", children: [_jsx(Typography, { variant: "subtitle1", sx: { fontWeight: 600 }, children: "Divisions" }), canModify && showActions && (_jsx(Button, { size: "small", startIcon: _jsx(AddIcon, {}), onClick: handleAddDivision, children: "Add Division" }))] }), canModify && showActions && hasChanges && (_jsxs(Stack, { direction: "row", spacing: 1, children: [_jsx(Button, { variant: "contained", onClick: handleSaveChanges, disabled: assignTeamsMutation.isLoading, children: "Save Changes" }), _jsx(Button, { variant: "outlined", onClick: handleResetChanges, disabled: assignTeamsMutation.isLoading, children: "Reset" })] })), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Each division needs at least 2 teams to generate fixtures." }), divisions.map((division) => {
                        const assignedTeams = (localDivisionAssignments[division.id] || [])
                            .slice()
                            .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
                        const count = assignedTeams.length;
                        return (_jsxs(Paper, { variant: "outlined", sx: { p: 2 }, children: [_jsxs(Stack, { direction: "row", alignItems: "center", gap: 1, sx: { mb: assignedTeams.length > 0 ? 1.5 : 0 }, children: [_jsx(Typography, { sx: { fontWeight: 600, flexGrow: 1, minWidth: 0 }, noWrap: true, children: division.name }), count < 2 && (
                                        // Non-colour cue that the division is under-strength, so the
                                        // warning isn't conveyed by the red text alone.
                                        _jsx(WarningAmberRoundedIcon, { fontSize: "small", color: "error", titleAccess: `${division.name} needs at least 2 teams` })), _jsxs(Typography, { variant: "body2", sx: {
                                                color: count < 2 ? "error.main" : "success.main",
                                                fontWeight: 500,
                                                whiteSpace: "nowrap",
                                            }, children: [count, " ", count === 1 ? "team" : "teams"] }), canModify && showActions && (_jsxs(_Fragment, { children: [_jsx(IconButton, { size: "small", onClick: () => handleEditDivision(division.id), "aria-label": `Edit ${division.name}`, children: _jsx(EditIcon, { fontSize: "small" }) }), _jsx(IconButton, { size: "small", onClick: () => handleDeleteDivision(division.id), "aria-label": `Delete ${division.name}`, children: _jsx(DeleteIcon, { fontSize: "small" }) })] }))] }), assignedTeams.length === 0 ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: "No teams assigned yet." })) : (_jsx(Stack, { spacing: 1, children: assignedTeams.map((team) => (_jsxs(Box, { sx: {
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                        }, children: [_jsx(TeamAvatar, { team: team, size: 28, showTooltip: false }), _jsx(Typography, { sx: { flexGrow: 1, minWidth: 0 }, noWrap: true, children: team.name }), canModify && (_jsx(IconButton, { size: "small", onClick: () => handleAssignTeam(team, null), "aria-label": `Remove ${team.name} from ${division.name}`, children: _jsx(CloseIcon, { fontSize: "small" }) }))] }, team.id))) }))] }, division.id));
                    }), canModify && (_jsxs(Box, { children: [_jsx(Typography, { variant: "subtitle1", sx: { fontWeight: 600, mb: 1.5 }, children: "Unassigned Teams" }), activeTeams.length === 0 ? (_jsx(EmptyState, { icon: _jsx(GroupsIcon, {}), heading: "No teams yet", description: "Add teams to this league to start assigning them to divisions.", actionLabel: "Add Teams", actionTo: addTeamsUrl })) : unassignedTeams.length === 0 ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: "All teams are assigned to divisions." })) : (_jsx(Stack, { spacing: 1, children: unassignedTeams.map((team) => (_jsxs(Paper, { variant: "outlined", sx: {
                                        p: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        flexWrap: "wrap",
                                    }, children: [_jsx(TeamAvatar, { team: team, size: 28, showTooltip: false }), _jsx(Typography, { sx: { flexGrow: 1, minWidth: 120 }, noWrap: true, children: team.name }), _jsx(Stack, { direction: "row", spacing: 0.5, flexWrap: "wrap", useFlexGap: true, children: divisions.map((division) => (_jsx(Button, { size: "small", variant: "outlined", onClick: () => handleAssignTeam(team, division.id), "aria-label": `Assign ${team.name} to ${division.name}`, children: division.name }, division.id))) })] }, team.id))) }))] }))] })), !!divisionsError && (_jsx(Alert, { severity: "error", sx: { mt: 2 }, children: "Failed to load divisions" })), !!teamsError && (_jsx(Alert, { severity: "error", sx: { mt: 2 }, children: "Failed to load teams" })), _jsx(DeleteDialog, { open: showDeleteDialog, title: `Delete ${divisions?.find((d) => d.id === divisionToDelete)?.name || "Division"}?`, description: "This action cannot be undone and will remove all team assignments from this division.", onCancel: () => {
                    setShowDeleteDialog(false);
                    setDivisionToDelete(null);
                }, onConfirm: handleConfirmDelete }), _jsxs(Dialog, { open: confirmDialog.open, onClose: () => handleConfirmClose(false), "aria-labelledby": "confirm-dialog-title", "aria-describedby": "confirm-dialog-description", PaperProps: {
                    sx: {
                        borderLeft: 4,
                        borderColor: "warning.main",
                    },
                }, children: [_jsxs(DialogTitle, { id: "confirm-dialog-title", sx: {
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            color: "warning.main",
                            fontWeight: "bold",
                        }, children: ["\u26A0\uFE0F ", confirmDialog.title] }), _jsx(DialogContent, { children: _jsx(DialogContentText, { id: "confirm-dialog-description", sx: { color: "text.primary", fontWeight: 500 }, children: confirmDialog.message }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: () => handleConfirmClose(false), color: "primary", children: "Cancel" }), _jsx(Button, { onClick: () => handleConfirmClose(true), color: "warning", variant: "contained", sx: { fontWeight: "bold" }, children: "Continue" })] })] })] }));
};
